<script lang="ts" setup>
import { computed, ref, type Component } from 'vue';
import { Primitive } from 'radix-vue';
import { PhX } from '@phosphor-icons/vue';
import { IconUi } from '../../icon';
import {
  chipFilterAvatar,
  chipFilterClearIcon,
  chipFilterLabel,
  chipFilterLeadingRecipe,
  chipFilterRecipe,
} from './chipFilterUiRecipe';

export interface ChipFilterProps {
  as?: string;
  /**
   * Selected state. Leave undefined to let the chip manage it internally,
   * or bind it (`v-model:selected`) to control it from the parent.
   */
  selected?: boolean;
  /** @deprecated use `selected` */
  isActive?: boolean;
  disabled?: boolean;
  /** Adds the `shadow.low` elevation from the design. */
  elevated?: boolean;
  /** Used to build the accessible label, e.g. "Add Groceries" / "Remove Groceries". */
  filterName?: string;
  leadingIcon?: Component | string;
  leadingAvatarSrc?: string;
  leadingAvatarAlt?: string;
}

const props = withDefaults(defineProps<ChipFilterProps>(), {
  as: 'button',
  // Explicit `undefined` keeps Vue from casting the absent boolean to `false`,
  // which is how the chip tells controlled from uncontrolled usage.
  selected: undefined,
  isActive: undefined,
  disabled: false,
  elevated: false,
  filterName: 'filter',
});

const emit = defineEmits<{
  'update:selected': [value: boolean];
}>();

const controlledValue = computed(() => props.selected ?? props.isActive);
const isControlled = computed(() => controlledValue.value !== undefined);

const internalSelected = ref(false);
const isSelected = computed(() =>
  isControlled.value ? Boolean(controlledValue.value) : internalSelected.value,
);

const leadingVariant = computed(() => {
  if (props.leadingAvatarSrc) {
    return 'avatar' as const;
  }
  return props.leadingIcon ? ('icon' as const) : ('none' as const);
});

// The icon and the spacer are decorative; an avatar with an alt text is not.
const isLeadingDecorative = computed(
  () => !(leadingVariant.value === 'avatar' && props.leadingAvatarAlt),
);

const ariaLabel = computed(() =>
  isSelected.value ? `Remove ${props.filterName}` : `Add ${props.filterName}`,
);

const toggle = () => {
  if (props.disabled) {
    return;
  }
  const next = !isSelected.value;
  if (!isControlled.value) {
    internalSelected.value = next;
  }
  emit('update:selected', next);
};
</script>

<template>
  <Primitive
    :as="props.as"
    class="chip-filter"
    :class="chipFilterRecipe({
      selected: isSelected,
      disabled: props.disabled,
      elevated: props.elevated,
    })"
    :type="props.as === 'button' ? 'button' : undefined"
    :disabled="props.as === 'button' ? props.disabled : undefined"
    :role="props.as === 'button' ? undefined : 'button'"
    :aria-pressed="isSelected"
    :aria-disabled="props.disabled || undefined"
    :aria-label="ariaLabel"
    :tabindex="props.disabled ? -1 : 0"
    @click="toggle"
  >
    <span
      class="chip-filter__leading"
      :class="chipFilterLeadingRecipe({ variant: leadingVariant })"
      :aria-hidden="isLeadingDecorative ? 'true' : undefined"
    >
      <img
        v-if="leadingVariant === 'avatar'"
        :src="props.leadingAvatarSrc"
        :alt="props.leadingAvatarAlt ?? ''"
        :class="chipFilterAvatar"
        loading="lazy"
        decoding="async"
      />
      <IconUi v-else-if="leadingVariant === 'icon'" :leading-icon="props.leadingIcon" />
    </span>

    <span class="chip-filter__label" :class="chipFilterLabel">
      <slot />
    </span>

    <span v-if="isSelected" class="chip-filter__clear" :class="chipFilterClearIcon">
      <IconUi :trailing-icon="PhX" />
    </span>
  </Primitive>
</template>
